import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Language {
    id: number;
    name: string;
    locale?: string;
}

export interface Localization {
    language: Language;
    name?: string;
}

export interface PrintDocumentTemplate {
    id: number;
    description: string;
}

export interface InvoiceType {
    id: number;
    name: string;
    printDocumentTemplate: PrintDocumentTemplate | null;
    localizations: Localization[];
}

export async function getInvoiceType(id: number): Promise<InvoiceType> {
    const result = await rms2Fetch<InvoiceType>(`invoicetypes/${id}`);
    if (!result) throw new Error('Empty response for getInvoiceType');
    return result;
}

export async function createInvoiceType(it: Partial<InvoiceType>): Promise<InvoiceType> {
    const result = await rms2Fetch<InvoiceType>(`invoicetypes/${it.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            name: it.name,
            printDocumentTemplateId: it.printDocumentTemplate?.id ?? null,
            localizations: it.localizations?.map((l) => ({
                languageId: l.language.id,
                name: l.name
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for createInvoiceType');
    return result;
}

export async function updateInvoiceType(it: InvoiceType): Promise<InvoiceType> {
    const result = await rms2Fetch<InvoiceType>(`invoicetypes/${it.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            name: it.name,
            printDocumentTemplateId: it.printDocumentTemplate?.id ?? null,
            localizations: it.localizations?.map((l) => ({
                languageId: l.language.id,
                name: l.name
            })) ?? []
        })
    });
    if (!result) throw new Error('Empty response for updateInvoiceType');
    return result;
}

// eslint-disable-next-line require-await
export async function deleteInvoiceType(id: number): Promise<void> {
    // Not supported — invoice types cannot be deleted
    throw new Error('Delete not supported for invoice types');
}

export async function getLanguages(): Promise<Language[]> {
    const response = await rms2Fetch<{ languages: Language[] }>('languages');
    return response?.languages ?? [];
}

export async function getPrintDocumentTemplates(): Promise<PrintDocumentTemplate[]> {
    const response = await rms2Fetch<{ printDocumentTemplates: PrintDocumentTemplate[] }>('invoicetypes/printDocumentTemplates');
    return response?.printDocumentTemplates ?? [];
}
